// Blacklist rules manager. Auto-applies to every public signup.
//
// Add-rule form at top, rules table below. Click a row to open
// a side panel with the rule's recent matches (existing
// subscribers whose email matches the rule today).

import { useCallback, useEffect, useState } from 'react';
import {
  Ban,
  Plus,
  Trash2,
  AlertTriangle,
  X,
  Pencil,
  Check,
  Globe,
  Mail,
  Regex,
  type LucideIcon,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { blacklistApi, ApiError } from '../lib/api';
import type {
  BlacklistRule,
  BlacklistRuleType,
  BlacklistRuleMatchingSubscriber,
  SubscriberStatus,
} from '@shared/types';

const TYPE_META: Record<
  BlacklistRuleType,
  { label: string; icon: LucideIcon; placeholder: string; help: string }
> = {
  email: {
    label: 'Email',
    icon: Mail,
    placeholder: 'someone@example.com',
    help: 'Matches one specific email address (case-insensitive).',
  },
  domain: {
    label: 'Domain',
    icon: Globe,
    placeholder: 'example.com',
    help: 'Matches every signup with that exact domain. Subdomains are not included — use a regex for those.',
  },
  regex: {
    label: 'Regex',
    icon: Regex,
    placeholder: '^.+@.+\\.ru$',
    help: 'JavaScript regex, evaluated case-insensitive against the full address. Anchor it yourself with ^…$.',
  },
};

const STATUS_BADGE: Record<SubscriberStatus, string> = {
  pending: 'bg-amber-100 text-amber-800',
  confirmed: 'bg-green-100 text-green-800',
  unsubscribed: 'bg-slate-200 text-slate-700',
  bounced: 'bg-red-100 text-red-800',
  blocked: 'bg-slate-300 text-slate-800',
};

const STATUS_LABEL: Record<SubscriberStatus, string> = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  unsubscribed: 'Unsubscribed',
  bounced: 'Bounced',
  blocked: 'Blocked',
};

export default function BlacklistPage() {
  const [rules, setRules] = useState<BlacklistRule[] | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoadErr(null);
      const { rules } = await blacklistApi.list();
      setRules(rules);
    } catch (e) {
      setLoadErr(e instanceof Error ? e.message : 'Failed to load rules');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-6">
      <div className="min-w-0">
        <div className="bg-white border border-slate-200 rounded-md p-4 sm:p-5 mb-6">
          <h2 className="text-sm font-medium text-slate-900 flex items-center gap-2 mb-1">
            <Ban size={14} className="text-slate-500" />
            How this works
          </h2>
          <p className="text-xs text-slate-600 leading-relaxed">
            Every signup on penobscotmorning.com is checked against
            these rules. A match looks like a successful signup to
            the visitor (same response, no error) but no welcome
            email is sent and the account is created with status
            <span className="mx-1 px-1 py-0.5 rounded bg-slate-200 text-slate-800 text-[10px] font-mono">
              blocked
            </span>
            . The match is recorded in that subscriber's activity
            timeline with the rule that caught them.
          </p>
        </div>

        <AddRuleForm onCreated={load} />

        {loadErr && (
          <p className="mt-4 text-sm text-red-600 flex items-center gap-2">
            <AlertTriangle size={14} /> {loadErr}
          </p>
        )}

        <div className="mt-6">
          {rules === null ? (
            <p className="text-sm text-slate-400">Loading…</p>
          ) : rules.length === 0 ? (
            <EmptyState />
          ) : (
            <ul className="bg-white border border-slate-200 rounded-md divide-y divide-slate-100">
              {rules.map((r) => (
                <RuleRow
                  key={r.id}
                  rule={r}
                  active={openId === r.id}
                  onSelect={() =>
                    setOpenId((cur) => (cur === r.id ? null : r.id))
                  }
                  onChanged={load}
                />
              ))}
            </ul>
          )}
        </div>
      </div>

      <aside className="min-w-0">
        {openId ? (
          <RuleDetailPanel
            ruleId={openId}
            onClose={() => setOpenId(null)}
            onChanged={load}
          />
        ) : (
          <div className="bg-white border border-slate-200 rounded-md p-5 text-sm text-slate-500">
            Select a rule to see who currently matches it and recent
            activity.
          </div>
        )}
      </aside>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="border-2 border-dashed border-slate-200 rounded-lg p-12 text-center">
      <Ban size={28} className="mx-auto text-slate-300 mb-2" />
      <p className="text-sm font-medium text-slate-700">
        No blacklist rules yet
      </p>
      <p className="text-xs text-slate-500 mt-1">
        Add a rule above to start filtering signups.
      </p>
    </div>
  );
}

function AddRuleForm({ onCreated }: { onCreated: () => void | Promise<void> }) {
  const [type, setType] = useState<BlacklistRuleType>('email');
  const [pattern, setPattern] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setSaving(true);
    try {
      await blacklistApi.create({
        rule_type: type,
        pattern,
        reason: reason.trim() || null,
      });
      setPattern('');
      setReason('');
      await onCreated();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to add rule');
    } finally {
      setSaving(false);
    }
  }

  const meta = TYPE_META[type];

  return (
    <form
      onSubmit={submit}
      className="bg-white border border-slate-200 rounded-md p-4 sm:p-5"
    >
      <h2 className="text-sm font-medium text-slate-900 mb-3">Add rule</h2>
      <div className="grid grid-cols-1 sm:grid-cols-[140px_minmax(0,1fr)] gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">
            Type
          </label>
          <select
            value={type}
            onChange={(e) => setType(e.target.value as BlacklistRuleType)}
            className="input"
          >
            {(Object.keys(TYPE_META) as BlacklistRuleType[]).map((t) => (
              <option key={t} value={t}>
                {TYPE_META[t].label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">
            Pattern
          </label>
          <input
            className="input w-full font-mono"
            value={pattern}
            onChange={(e) => setPattern(e.target.value)}
            placeholder={meta.placeholder}
            spellCheck={false}
            autoCorrect="off"
            autoCapitalize="off"
            required
          />
          <p className="text-[11px] text-slate-500 mt-1">{meta.help}</p>
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs font-medium text-slate-600 mb-1">
            Reason <span className="text-slate-400 font-normal">(optional)</span>
          </label>
          <input
            className="input w-full"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why is this here? Shows up in the subscriber's activity log."
            maxLength={500}
          />
        </div>
        <div className="sm:col-span-2 flex items-center gap-3">
          <button
            type="submit"
            disabled={saving || !pattern.trim()}
            className="btn-primary"
          >
            <Plus size={14} />
            {saving ? 'Adding…' : 'Add rule'}
          </button>
          {err && (
            <span className="text-sm text-red-600 flex items-center gap-1">
              <AlertTriangle size={14} /> {err}
            </span>
          )}
        </div>
      </div>
    </form>
  );
}

function RuleRow({
  rule,
  active,
  onSelect,
  onChanged,
}: {
  rule: BlacklistRule;
  active: boolean;
  onSelect: () => void;
  onChanged: () => void | Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const meta = TYPE_META[rule.rule_type];
  const Icon = meta.icon;

  async function toggleEnabled(e: React.MouseEvent) {
    e.stopPropagation();
    setBusy(true);
    try {
      await blacklistApi.update(rule.id, {
        enabled: !rule.enabled,
        expected_updated_at: rule.updated_at,
      });
      await onChanged();
    } catch (err) {
      alert(
        err instanceof ApiError && err.code === 'conflict'
          ? 'Rule changed elsewhere. Reload.'
          : err instanceof Error
            ? err.message
            : 'Failed',
      );
    } finally {
      setBusy(false);
    }
  }

  async function doDelete(e: React.MouseEvent) {
    e.stopPropagation();
    setBusy(true);
    try {
      await blacklistApi.remove(rule.id);
      await onChanged();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed');
    } finally {
      setBusy(false);
      setConfirmDelete(false);
    }
  }

  return (
    <li
      className={`flex items-center gap-3 px-4 sm:px-5 py-3 cursor-pointer hover:bg-slate-50 ${
        active ? 'bg-blue-50/40' : ''
      } ${rule.enabled ? '' : 'opacity-60'}`}
      onClick={onSelect}
    >
      <span
        className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center bg-slate-100`}
      >
        <Icon size={14} className="text-slate-600" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[10px] uppercase tracking-wide text-slate-500">
            {meta.label}
          </span>
          <code className="font-mono text-sm text-slate-900 truncate">
            {rule.pattern}
          </code>
        </div>
        {rule.reason && (
          <p className="text-xs text-slate-500 mt-0.5 truncate">{rule.reason}</p>
        )}
      </div>
      <div className="text-xs text-slate-500 flex-shrink-0 hidden sm:block text-right">
        <div>
          {rule.match_count} match{rule.match_count === 1 ? '' : 'es'}
        </div>
        <div>
          {rule.last_matched_at
            ? new Date(rule.last_matched_at).toLocaleDateString()
            : '—'}
        </div>
      </div>
      <button
        onClick={toggleEnabled}
        disabled={busy}
        className={`text-xs px-2 py-1 rounded border ${
          rule.enabled
            ? 'border-green-200 text-green-800 bg-green-50'
            : 'border-slate-200 text-slate-600 bg-white'
        }`}
        title={rule.enabled ? 'Disable rule' : 'Enable rule'}
      >
        {rule.enabled ? 'On' : 'Off'}
      </button>
      {confirmDelete ? (
        <span className="flex items-center gap-1">
          <button
            onClick={doDelete}
            disabled={busy}
            className="text-xs px-2 py-1 rounded bg-red-600 text-white"
          >
            Confirm
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setConfirmDelete(false);
            }}
            className="text-xs px-1.5 py-1 rounded text-slate-500 hover:bg-slate-100"
          >
            <X size={12} />
          </button>
        </span>
      ) : (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setConfirmDelete(true);
          }}
          className="text-slate-400 hover:text-red-600 p-1"
          title="Delete rule"
        >
          <Trash2 size={14} />
        </button>
      )}
    </li>
  );
}

function RuleDetailPanel({
  ruleId,
  onClose,
  onChanged,
}: {
  ruleId: string;
  onClose: () => void;
  onChanged: () => void | Promise<void>;
}) {
  const [rule, setRule] = useState<BlacklistRule | null>(null);
  const [matching, setMatching] = useState<BlacklistRuleMatchingSubscriber[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [editing, setEditing] = useState(false);
  const [editPattern, setEditPattern] = useState('');
  const [editReason, setEditReason] = useState('');
  const [saveErr, setSaveErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await blacklistApi.get(ruleId);
      setRule(r.rule);
      setMatching(r.matching_subscribers);
      setTruncated(r.truncated);
      setEditPattern(r.rule.pattern);
      setEditReason(r.rule.reason ?? '');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed');
    } finally {
      setLoading(false);
    }
  }, [ruleId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function save() {
    if (!rule) return;
    setSaveErr(null);
    setSaving(true);
    try {
      await blacklistApi.update(rule.id, {
        pattern: editPattern,
        reason: editReason.trim() || null,
        expected_updated_at: rule.updated_at,
      });
      setEditing(false);
      await load();
      await onChanged();
    } catch (e) {
      setSaveErr(
        e instanceof ApiError && e.code === 'conflict'
          ? 'Rule changed elsewhere. Close and reopen.'
          : e instanceof Error
            ? e.message
            : 'Save failed',
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 sm:p-5 sticky top-6">
      <header className="flex items-start justify-between mb-3">
        <h2 className="text-sm font-medium text-slate-900">Rule details</h2>
        <button
          onClick={onClose}
          className="text-slate-400 hover:text-slate-700"
          title="Close"
        >
          <X size={16} />
        </button>
      </header>

      {loading ? (
        <p className="text-sm text-slate-400">Loading…</p>
      ) : err || !rule ? (
        <p className="text-sm text-red-600">{err ?? 'Failed'}</p>
      ) : (
        <>
          <dl className="text-xs space-y-2 mb-4">
            <div>
              <dt className="uppercase tracking-wide text-slate-500">Type</dt>
              <dd className="text-slate-800">{TYPE_META[rule.rule_type].label}</dd>
            </div>
            <div>
              <dt className="uppercase tracking-wide text-slate-500">Pattern</dt>
              <dd>
                {editing ? (
                  <input
                    className="input w-full font-mono mt-1"
                    value={editPattern}
                    onChange={(e) => setEditPattern(e.target.value)}
                  />
                ) : (
                  <code className="font-mono text-sm text-slate-900 break-all">
                    {rule.pattern}
                  </code>
                )}
              </dd>
            </div>
            <div>
              <dt className="uppercase tracking-wide text-slate-500">Reason</dt>
              <dd>
                {editing ? (
                  <input
                    className="input w-full mt-1"
                    value={editReason}
                    onChange={(e) => setEditReason(e.target.value)}
                    placeholder="Why this rule exists"
                    maxLength={500}
                  />
                ) : (
                  <span className="text-slate-700">{rule.reason ?? '—'}</span>
                )}
              </dd>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <dt className="uppercase tracking-wide text-slate-500">Matches</dt>
                <dd className="text-slate-800">{rule.match_count}</dd>
              </div>
              <div>
                <dt className="uppercase tracking-wide text-slate-500">
                  Last match
                </dt>
                <dd className="text-slate-800">
                  {rule.last_matched_at
                    ? new Date(rule.last_matched_at).toLocaleString()
                    : '—'}
                </dd>
              </div>
            </div>
            <div>
              <dt className="uppercase tracking-wide text-slate-500">Created</dt>
              <dd className="text-slate-800">
                {new Date(rule.created_at).toLocaleString()}
              </dd>
            </div>
          </dl>

          {saveErr && (
            <p className="text-xs text-red-600 mb-2 flex items-center gap-1">
              <AlertTriangle size={12} /> {saveErr}
            </p>
          )}

          <div className="flex items-center gap-2 mb-5">
            {editing ? (
              <>
                <button
                  onClick={save}
                  disabled={saving}
                  className="btn-primary text-xs !py-1.5 !px-2.5"
                >
                  <Check size={12} /> {saving ? 'Saving…' : 'Save'}
                </button>
                <button
                  onClick={() => {
                    setEditing(false);
                    setEditPattern(rule.pattern);
                    setEditReason(rule.reason ?? '');
                    setSaveErr(null);
                  }}
                  disabled={saving}
                  className="btn-ghost text-xs !py-1.5 !px-2.5"
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                onClick={() => setEditing(true)}
                className="btn-secondary text-xs !py-1.5 !px-2.5"
              >
                <Pencil size={12} /> Edit
              </button>
            )}
          </div>

          <div>
            <h3 className="text-xs uppercase tracking-wide text-slate-500 mb-2">
              Existing subscribers matching this rule
            </h3>
            {matching.length === 0 ? (
              <p className="text-xs text-slate-400">
                No existing subscribers match.
              </p>
            ) : (
              <ul className="border border-slate-100 rounded divide-y divide-slate-100 max-h-72 overflow-y-auto">
                {matching.map((s) => (
                  <li key={s.id} className="px-2.5 py-2 text-xs">
                    <div className="flex items-center gap-2 min-w-0">
                      <Link
                        to={`/subscribers/${s.id}`}
                        className="text-blue-700 hover:underline truncate min-w-0"
                      >
                        {s.email}
                      </Link>
                      <span
                        className={`px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0 ${STATUS_BADGE[s.status]}`}
                      >
                        {STATUS_LABEL[s.status]}
                      </span>
                    </div>
                    <p className="text-slate-500 mt-0.5">
                      via{' '}
                      <code className="text-[10px] bg-slate-100 px-1 rounded">
                        {s.source_slug}
                      </code>{' '}
                      · {new Date(s.created_at).toLocaleDateString()}
                    </p>
                  </li>
                ))}
              </ul>
            )}
            {truncated && (
              <p className="text-[11px] text-slate-500 mt-1.5">
                Showing the first 50 matches. Refine the rule to narrow.
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
